// 🆕 [Phase 56.2] Extrait de CalendrierProduction.jsx — bandeau d'action du calendrier.
// Composant autonome qui gère :
// • Les 8 boutons d'action (Météo / Congés / RDV / Sans job / Booking / Projets pluie /
//   Route lavage / Message horaire)
// • L'ancrage favoris (étoile) — persisté en localStorage, clé inchangée
//   `calendrier_pinned_btns_v1` pour préserver les préférences user au déploiement.
// • Le menu « ⋯ Plus » avec popover pour les boutons non épinglés.
//
// Architecture des props : un seul objet `panels` qui regroupe l'état + handler de chaque
// bouton — limite le prop drilling à 1 prop top-level.
import { useState } from 'react';
import { Cloud, TreePine, Calendar as CalendarIcon, AlertTriangle, Clock, MapPin, MessageCircle, Star, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const PINNED_BTNS_KEY = 'calendrier_pinned_btns_v1';
const DEFAULT_PINNED = ['sans_job', 'booking', 'meteo'];

function CalendarActionBar({ panels }) {
  // 🆕 2026-05-14 — Système d'ancrage favoris pour la barre d'action.
  //   Chaque utilisateur peut épingler ses boutons préférés ; les autres
  //   sont masqués derrière un menu « ⋯ Plus ». Persisté en localStorage.
  const [pinnedBtns, setPinnedBtns] = useState(() => {
    try {
      const raw = localStorage.getItem(PINNED_BTNS_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch (_e) { /* ignore */ }
    return new Set(DEFAULT_PINNED);
  });
  const togglePinBtn = (btnId) => {
    setPinnedBtns(prev => {
      const next = new Set(prev);
      if (next.has(btnId)) next.delete(btnId);
      else next.add(btnId);
      try { localStorage.setItem(PINNED_BTNS_KEY, JSON.stringify([...next])); } catch (_e) { /* ignore */ }
      return next;
    });
  };
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const { meteo, conges, agenda, sans_job, booking, projets_pluie, route_lavage, message_horaire } = panels;
  const hasNoJob = (sans_job.count || 0) > 0;
  const hasBooking = (booking.count || 0) > 0;

  // Définition de tous les boutons disponibles
  const allButtons = [
    {
      id: 'meteo', label: 'Météo', icon: <Cloud className="w-3.5 h-3.5" />,
      active: meteo.active, onClick: meteo.onToggle,
      activeCls: 'bg-blue-100 text-blue-700 border-blue-300',
      idleCls: 'text-gray-600 border-gray-200 hover:bg-slate-50',
      title: meteo.active ? 'Cacher la météo' : 'Afficher la météo',
    },
    {
      id: 'conges', label: 'Congés', icon: <TreePine className="w-3.5 h-3.5" />,
      active: conges.active, onClick: conges.onToggle,
      activeCls: 'bg-purple-100 text-purple-700 border-purple-300',
      idleCls: 'text-gray-600 border-gray-200 hover:bg-slate-50',
      title: conges.active ? 'Cacher les congés' : 'Afficher les congés',
    },
    {
      id: 'agenda', label: 'RDV', icon: <CalendarIcon className="w-3.5 h-3.5" />,
      active: agenda.active, onClick: agenda.onToggle,
      activeCls: 'bg-violet-100 text-violet-700 border-violet-300',
      idleCls: 'text-gray-600 border-gray-200 hover:bg-slate-50',
      title: agenda.active ? 'Cacher les RDV' : 'Afficher les RDV',
    },
    {
      id: 'sans_job', label: 'Sans job', icon: <AlertTriangle className="w-3.5 h-3.5" />,
      active: sans_job.active, onClick: sans_job.onToggle,
      activeCls: hasNoJob ? 'bg-amber-100 text-amber-800 border-amber-400 animate-pulse' : 'bg-emerald-100 text-emerald-700 border-emerald-300',
      idleCls: hasNoJob ? 'bg-amber-50 text-amber-800 border-amber-400 animate-pulse hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100',
      badge: sans_job.count,
      badgeCls: hasNoJob ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900',
      title: 'Employés sans BT assigné pour les 14 prochains jours',
    },
    {
      id: 'booking', label: 'Booking', icon: <Clock className="w-3.5 h-3.5" />,
      active: booking.active, onClick: booking.onToggle,
      activeCls: hasBooking ? 'bg-orange-100 text-orange-800 border-orange-400 animate-pulse' : 'bg-emerald-100 text-emerald-700 border-emerald-300',
      idleCls: hasBooking ? 'bg-orange-50 text-orange-800 border-orange-400 animate-pulse hover:bg-orange-100' : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100',
      badge: booking.count,
      badgeCls: hasBooking ? 'bg-orange-200 text-orange-900' : 'bg-emerald-200 text-emerald-900',
      title: 'BT dont le nombre de jours cédulés diverge du recommandé',
    },
    {
      id: 'projets_pluie', label: 'Projets pluie', icon: <Cloud className="w-3.5 h-3.5" />,
      active: false, onClick: projets_pluie.onOpen,
      activeCls: '', idleCls: 'text-blue-700 border-blue-300 hover:bg-blue-50',
      title: 'Projets prévus pour les jours de pluie',
    },
    {
      id: 'route_lavage', label: 'Route lavage', icon: <MapPin className="w-3.5 h-3.5" />,
      active: route_lavage.active, onClick: route_lavage.onToggle,
      activeCls: 'bg-emerald-50 text-emerald-700 border-emerald-300',
      idleCls: 'text-emerald-700 border-emerald-300 hover:bg-emerald-50',
      title: 'Créer une route de lavage',
    },
    {
      id: 'message_horaire', label: 'Message', icon: <MessageCircle className="w-3.5 h-3.5" />,
      active: false, onClick: message_horaire.onOpen,
      activeCls: '', idleCls: 'text-fuchsia-700 border-fuchsia-300 hover:bg-fuchsia-50',
      title: "Générer le message d'horaire à coller dans Messenger",
    },
  ];

  const pinnedList = allButtons.filter(b => pinnedBtns.has(b.id));
  const unpinnedList = allButtons.filter(b => !pinnedBtns.has(b.id));

  const renderBtn = (btn, inMenu = false) => (
    <div key={btn.id} className={inMenu ? 'flex items-center gap-1' : 'flex items-center gap-0.5'}>
      <Button
        variant="outline"
        size="sm"
        className={`flex items-center gap-1.5 text-xs h-9 ${btn.active ? btn.activeCls : btn.idleCls}`}
        onClick={() => { btn.onClick(); if (inMenu) setShowMoreMenu(false); }}
        data-testid={`btn-${btn.id}`}
        title={btn.title}
      >
        {btn.icon}
        {btn.label}
        {btn.badge !== undefined && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${btn.badgeCls}`}>
            {btn.badge}
          </span>
        )}
      </Button>
      {inMenu && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); togglePinBtn(btn.id); }}
          className="text-amber-400 hover:text-amber-600 p-1 rounded transition"
          title="Épingler dans la barre"
          data-testid={`btn-pin-${btn.id}`}
        >
          <Star className="w-4 h-4" />
        </button>
      )}
      {!inMenu && pinnedList.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); togglePinBtn(btn.id); }}
          className="text-amber-500 hover:text-amber-700 p-0.5 rounded transition opacity-0 hover:opacity-100 group-hover:opacity-60"
          title="Désépingler"
          data-testid={`btn-unpin-${btn.id}`}
        >
          <Star className="w-3 h-3 fill-current" />
        </button>
      )}
    </div>
  );

  return (
    <div className="flex gap-1.5 flex-wrap items-center group">
      {pinnedList.map(b => renderBtn(b, false))}
      {unpinnedList.length > 0 && (
        <Popover open={showMoreMenu} onOpenChange={setShowMoreMenu}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-xs h-9 text-slate-700 border-slate-300 hover:bg-slate-100"
              data-testid="btn-more-actions"
              title="Plus d'actions — clic sur l'étoile pour épingler"
            >
              <MoreHorizontal className="w-3.5 h-3.5" />
              Plus
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700">
                {unpinnedList.length}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="end">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 px-2 py-1">
              Plus d'actions
            </p>
            <p className="text-[10px] text-slate-400 px-2 pb-2 leading-snug">
              Cliquez sur ⭐ pour épingler dans la barre principale.
            </p>
            <div className="space-y-1">
              {unpinnedList.map(b => renderBtn(b, true))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export default CalendarActionBar;
